import React, { FC, useContext } from 'react';

import { ErrorBoundary } from 'client/blocks/ErrorBoundary/ErrorBoundary';
import { AppCard } from 'client/blocks-redesign/AppCard/AppCard';
import { PageLoader } from 'client/blocks-redesign/Loader/PageLoader';
import { OperationEventsWithName } from 'client/blocks-redesign/OperationEvents/OperationEventsWithName';
import { Context as SnapshotContext } from 'client/context/snapshotContext';
import { Operation, OperationEvent } from 'client/model/operation';
import { Channel, IAppCard, Skill } from 'client/model/skill';

import { AddNewAppCard } from '../AddNewAppCard/AddNewAppCard';

import styles from './AppCardList.module.scss';

interface Props {
    channel: Channel;
}

export const SkillsCardList: FC<Props> = ({ channel }) => {
    const { snapshot } = useContext(SnapshotContext);

    if (!snapshot) {
        return <PageLoader />;
    }

    const { operations, skills } = snapshot;

    const skillsById = skills.reduce(
        (acc, skill) => {
            acc[skill.id] = skill;
            return acc;
        },
        {} as Record<string, Skill>,
    );

    const getOperationName = (operation: Operation) => {
        const skill = operation.itemId ? skillsById[operation.itemId] : undefined;

        if (skill && skill.name) {
            return skill.name;
        }

        if (skill && skill.draft && skill.draft.name) {
            return skill.draft.name;
        }

        return operation?.itemName || '';
    };

    const getOperationId = (operation: Operation) => {
        const skill = operation.itemId ? skillsById[operation.itemId] : undefined;

        return skill ? operation.itemId : undefined;
    };

    const getSkillChannel = (operation: Operation) => {
        const skill = operation.itemId ? skillsById[operation.itemId] : undefined;

        return skill?.channel || Channel.AliceSkill;
    };

    const events: OperationEvent<React.ReactNode>[] = operations.slice(0, 5).map((operation) => {
        const channel = getSkillChannel(operation) ?? Channel.AliceSkill;

        return {
            skillEntity: {
                name: getOperationName(operation),
                id: getOperationId(operation),
                channel,
            },
            operation: {
                ...operation,
                comment: operation?.comment || '',
            },
        };
    });

    const cards: IAppCard[] = snapshot.skills
        .filter((skill) => skill.channel === channel)
        .map((skill) => ({
            id: skill.id,
            title: skill.draft.name,
            channel: skill.channel,
            onAir: skill.onAir,
            publishedAt: skill.updatedAt,
            logoUrl: skill.draft?.logo2?.url || '',
            lastOperation: snapshot.operations.find((operation) => operation.id === skill.id),
        }));

    return (
        <ErrorBoundary withHomepageLink>
            <div className={styles.installableAppCardListContainer}>
                <div className={styles.installableAppCardList}>
                    <AddNewAppCard aliceAppsCount={cards.length} channel={channel} />
                    {cards.map((card) => (
                        <AppCard key={card.id} card={card} />
                    ))}
                </div>
                <OperationEventsWithName events={events} />
            </div>
        </ErrorBoundary>
    );
};
